import Seo from "@/components/Seo";

const PrivacyPolicy = () => {
  return (
    <>
      <Seo
        title="Privacy Policy - MyFence.com"
        description="Learn about MyFence.com's privacy policy and how we protect your personal information when you use our fencing services."
        canonical="/privacy-policy"
      />
      
      <main className="min-h-screen bg-background py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy for Text Messaging – MyFence.com</h1>
            <p className="text-lg text-muted-foreground">Effective Date: August 7, 2025</p>
          </header>

          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <p className="text-muted-foreground mb-6">
                MyFence.com, a family-owned fence contractor based in Maple Valley, Washington, is committed to protecting the privacy of our customers. This Privacy Policy outlines how we collect, use, and safeguard your personal information when you interact with us via text messaging. We do not share your personal information with third parties for marketing or other purposes, except as required by law or as necessary to provide our services.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Information We Collect</h2>
              <p className="text-muted-foreground mb-4">
                When you engage with MyFence.com via text messaging, we may collect the following:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Phone Number:</strong> The mobile number you provide to communicate with us.</li>
                <li><strong>Message Content:</strong> Information you share in text messages, such as inquiries about fence installation, project details, or scheduling preferences.</li>
                <li><strong>Communication Metadata:</strong> Details like the date, time, and frequency of text messages.</li>
              </ul>
              <p className="text-muted-foreground mb-4">We collect this information when you:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Opt-in to receive text messages from us (e.g., for appointment reminders, project updates, or promotional offers).</li>
                <li>Send us a text message directly to our contact number, (253) 455-1885.</li>
                <li>Submit a virtual bid request that includes your phone number for follow-up communication.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">
                We use the information collected via text messaging solely to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Respond to your inquiries about our fencing services, such as cedar fence installations, gate designs, or project estimates.</li>
                <li>Send appointment confirmations, reminders, or updates about your fence project.</li>
                <li>Provide occasional promotional offers related to our services, such as discounts on fence replacements or storm damage repairs, if you have opted in to receive such messages.</li>
                <li>Improve our customer service and communication processes.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. Our Commitment to Not Sharing Your Information</h2>
              <p className="text-muted-foreground mb-4">
                MyFence.com does not sell, trade, or share your personal information, including your phone number and text message content, with any third parties for marketing or commercial purposes. Your information is used exclusively by MyFence.com to provide and improve our fencing services. Exceptions to this policy occur only when:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Required by Law:</strong> We may disclose your information to comply with legal obligations, such as responding to a court order or subpoena.</li>
                <li><strong>Service Providers:</strong> We may share your phone number with trusted service providers who assist with our text messaging operations (e.g., SMS platform providers). These providers are contractually obligated to protect your information and use it only for the purpose of delivering our messages.</li>
                <li><strong>Business Transitions:</strong> In the unlikely event of a merger, acquisition, or sale of MyFence.com, your information may be transferred as part of the business assets, but it will remain subject to this Privacy Policy unless you are notified otherwise.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. Opting In and Out of Text Messages</h2>
              <p className="text-muted-foreground mb-4">
                By providing your phone number and initiating or responding to text messages, you consent to receive communications from MyFence.com related to your fencing project or our services. You may opt out of receiving text messages at any time by:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Replying "STOP" to any text message you receive from us.</li>
                <li>Contacting us directly at info@myfence.com or (253) 455-1885 to request removal from our texting list.</li>
              </ul>
              <p className="text-muted-foreground">
                Upon opting out, we will cease sending you text messages, though we may retain your information as necessary for record-keeping or legal compliance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. Data Security</h2>
              <p className="text-muted-foreground">
                We take reasonable measures to protect your personal information from unauthorized access, loss, or misuse. Text messages are sent through secure SMS platforms, and any stored data is protected using industry-standard safeguards. However, please note that no method of electronic communication is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your phone number and text message data only for as long as necessary to fulfill the purposes outlined in this policy, such as completing your fence project or complying with legal requirements. Once no longer needed, your information is securely deleted or anonymized.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Your Rights</h2>
              <p className="text-muted-foreground mb-4">
                As a customer, you have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Request access to the personal information we hold about you via text messaging.</li>
                <li>Request correction of any inaccurate information.</li>
                <li>Request deletion of your information, subject to legal or contractual obligations.</li>
              </ul>
              <p className="text-muted-foreground">
                To exercise these rights, please contact us at info@myfence.com or (253) 455-1885.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Third-Party Links</h2>
              <p className="text-muted-foreground">
                Our text messages may occasionally include links to our website (https://www.myfence.com/) or social media platforms (e.g., Facebook, Instagram). This Privacy Policy applies only to text messaging interactions with MyFence.com. Please review the privacy policies of any linked sites for information on their practices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Changes to This Privacy Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The updated policy will be posted on our website at https://www.myfence.com/, and the effective date will be revised accordingly. If significant changes are made, we may notify you via text message or email.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">10. Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                If you have questions or concerns about this Privacy Policy or our text messaging practices, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-muted-foreground">Email: info@myfence.com</p>
                <p className="text-muted-foreground">Phone: (253) 455-1885</p>
                <p className="text-muted-foreground">Address: MyFence.com, Maple Valley, Washington</p>
              </div>
              <p className="text-muted-foreground mt-4">
                Thank you for choosing MyFence.com for your fencing needs. We value your trust and are committed to protecting your privacy.
              </p>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default PrivacyPolicy;